// Per-document review. The real plan is per ITEM (each item is one record the
// pipeline proposes to write), and the approve endpoint takes item-level
// decisions. accept/edit/reject operate on items.
//
// Every value here comes from GET /jobs/:id/plan and GET /jobs/:id. Nothing is
// written until Approve, and a critical-risk plan needs a second confirmation
// the server enforces.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchJob, fetchPlan, approvePlan, dismissJob } from './ingestApi';
import AnnotationLayer from './AnnotationLayer';

const humanize = (s: string | undefined) => String(s || '').replace(/_/g, ' ');
const pct = (c: number | null | undefined) => (c == null ? null : `${Math.round(Number(c) * 100)}%`);

const money = (n: number | unknown) =>
  typeof n === 'number' ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : n;

const fmtValue = (key: string, val: unknown) => {
  if (val == null || val === '') return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  if (/amount|total|subtotal|price|rate|_impact/i.test(key) && typeof val === 'number') return money(val);
  return String(val);
};

const TARGET_LABELS: Record<string, string> = {
  invoices: 'Invoice',
  invoice_line_items: 'Line item',
  personnel: 'Personnel',
  contract_mods: 'Contract modification',
  risks: 'Risk',
};
const targetLabel = (item: { target_table: string }) => TARGET_LABELS[item.target_table] || humanize(item.target_table);

function itemFields(item: { proposed_value: any; target_field?: string }) {
  const pv = item.proposed_value;
  if (pv && typeof pv === 'object' && !Array.isArray(pv)) {
    return Object.entries(pv).filter(([, v]) => v !== null && v !== undefined && v !== '');
  }
  return [[item.target_field || 'value', pv]];
}

function blastText(radius: any): string | null {
  if (!radius || typeof radius !== 'object') return null;
  const parts: string[] = [];
  if (radius.contracts?.length) parts.push(`contract ${radius.contracts.join(', ')}`);
  if (radius.clins?.length) parts.push(`CLIN ${radius.clins.join(', ')}`);
  if (radius.projects?.length) parts.push(`${radius.projects.length} project${radius.projects.length === 1 ? '' : 's'}`);
  if (radius.personnel?.length) parts.push(`${radius.personnel.length} on the roster`);
  if (radius.financial_impact?.amount != null) parts.push(`${money(radius.financial_impact.amount)} ${radius.financial_impact.type || ''}`.trim());
  return parts.length ? `Affects ${parts.join(' · ')}.` : null;
}

const REC_TONE: Record<string, string> = { approve: 'low', hold: 'elevated', reject: 'critical' };
const REC_VERB: Record<string, string> = { approve: 'Recommend approving', hold: 'Recommend a hold', reject: 'Recommend rejecting' };

interface IngestReviewProps {
  jobId: string;
  onBack: () => void;
  onToast?: (msg: string) => void;
  onChanged?: () => void;
  readOnly?: boolean;
}

export default function IngestReview({ jobId, onBack, onToast, onChanged, readOnly = false }: IngestReviewProps) {
  const [job, setJob] = useState<any>(null);
  const [plan, setPlan] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [decisions, setDecisions] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, any>>({});
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [{ job: j }, planRes] = await Promise.all([
          fetchJob(jobId),
          fetchPlan(jobId).catch(() => ({ plan: null, items: [] })),
        ]);
        if (cancelled) return;
        setJob(j);
        setPlan(planRes.plan);
        setItems(planRes.items || []);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  const decisionOf = (id: string) => decisions[id] || 'accepted';
  const rejectedCount = items.filter((it) => decisionOf(it.id) === 'rejected').length;
  const acceptedCount = items.length - rejectedCount;

  const setDecision = useCallback((id: string, status: string) => {
    setDecisions((prev) => ({ ...prev, [id]: prev[id] === status ? 'accepted' : status }));
    setConfirming(false);
  }, []);

  const startEdit = useCallback((item: any) => {
    setEditing(item.id);
    setEdits((prev) => ({ ...prev, [item.id]: prev[item.id] || { ...item.proposed_value } }));
    setDecisions((prev) => ({ ...prev, [item.id]: 'accepted' }));
    setConfirming(false);
  }, []);

  const editField = useCallback((id: string, key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  }, []);

  const isCritical = plan?.risk_class === 'critical';
  const blast = useMemo(() => blastText(plan?.blast_radius), [plan]);
  const blockers = Array.isArray(plan?.blocking_errors) ? plan.blocking_errors : [];
  const blocked = blockers.length > 0;

  const approve = useCallback(async () => {
    if (blocked || submitting) return;
    if (acceptedCount === 0) { onToast?.('Nothing to approve — every item is rejected.'); return; }
    if (isCritical && !confirming) {
      setConfirming(true);
      onToast?.('Critical change — press Approve again to confirm.');
      return;
    }
    setSubmitting(true);
    try {
      const itemDecisions = items.map((it) => {
        const status = decisionOf(it.id);
        if (status === 'rejected') return { itemId: it.id, status: 'rejected' };
        if (editing === it.id || edits[it.id]) return { itemId: it.id, status: 'edited', editedValue: edits[it.id] || it.proposed_value };
        return { itemId: it.id, status: 'accepted' };
      });
      const res = await approvePlan(jobId, { itemDecisions, secondConfirmation: isCritical });
      onToast?.(`${res.accepted + res.edited} change${res.accepted + res.edited === 1 ? '' : 's'} written · ${res.rejected} rejected`);
      onChanged?.();
      onBack?.();
    } catch (e: any) {
      if (e.code === 'second_confirmation_required') { setConfirming(true); onToast?.('Critical change — press Approve again to confirm.'); }
      else onToast?.(e.message || 'Approval failed');
    } finally {
      setSubmitting(false);
    }
  }, [blocked, submitting, acceptedCount, isCritical, confirming, items, editing, edits, jobId, onToast, onChanged, onBack]);

  const applySchedule = useCallback(async () => {
    if (blocked || submitting) return;
    if (isCritical && !confirming) {
      setConfirming(true);
      onToast?.('Critical change — press Approve again to confirm.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await approvePlan(jobId, { itemDecisions: [], secondConfirmation: isCritical });
      onToast?.(`${res.accepted + res.edited} change${res.accepted + res.edited === 1 ? '' : 's'} written · ${res.rejected} rejected`);
      onChanged?.();
      onBack?.();
    } catch (e: any) {
      if (e.code === 'second_confirmation_required') { setConfirming(true); onToast?.('Critical change — press Approve again to confirm.'); }
      else onToast?.(e.message || 'Approval failed');
    } finally {
      setSubmitting(false);
    }
  }, [blocked, submitting, isCritical, confirming, jobId, onToast, onChanged, onBack]);

  const rejectDocument = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await dismissJob(jobId);
      onToast?.(`${job?.original_filename || 'Document'} rejected — nothing was written.`);
      onChanged?.();
      onBack?.();
    } catch (e: any) {
      onToast?.(e.message || 'Could not reject the document');
    } finally {
      setSubmitting(false);
    }
  }, [submitting, jobId, job, onToast, onChanged, onBack]);

  if (loading) {
    return (
      <>
        <button className="ingest-back" onClick={onBack}>← Ingest queue</button>
        <div className="ingest-review"><div className="state"><p className="state__body">Loading document…</p></div></div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <button className="ingest-back" onClick={onBack}>← Ingest queue</button>
        <div className="ingest-review"><div className="state"><p className="state__body">Could not load this document — {error}</p></div></div>
      </>
    );
  }

  if (job?.doc_type === 'schedule') {
    return (
      <>
        <button className="ingest-back" onClick={onBack}>← Ingest queue</button>
        <div className="ingest-review ing-sched-review" style={{ gridTemplateColumns: '1fr' }}>
          <div className="ingest-rev-head ing-navy-head">
            <span className="ingest-rev-name" title={job?.original_filename}>{job?.original_filename}</span>
            {plan?.risk_class ? <span className={`ingest-chip ingest-chip-${plan.risk_class}`}>{plan.risk_class}</span> : null}
            <span className="ingest-chip ingest-chip-info">IMS</span>
          </div>
          {blast ? (
            <div className="ingest-blast">
              <span className="ingest-blast-lab">Blast radius</span>
              <span>{blast}</span>
            </div>
          ) : null}
          <div className="ing-sched-tiles">
            <div className="ing-tile">
              <span className="ing-tile-l">Risk</span>
              <span className="ing-tile-v" style={{ fontSize: 20 }}>{plan?.risk_class ? humanize(plan.risk_class) : '—'}</span>
            </div>
          </div>
          <div className="ingest-rev-foot">
            {readOnly ? (
              <>
                <span className="ingest-foot-note">
                  {job?.status === 'committed' ? 'Committed · schedule revision applied' : humanize(job?.status || 'settled')}
                </span>
                <span className="ingest-foot-push" />
                <button className="btn btn--primary" onClick={onBack}>Back to queue</button>
              </>
            ) : (
              <>
                <span className="ingest-foot-note">Baseline stays as-is · re-baselining is a separate approval</span>
                <span className="ingest-foot-push" />
                <button className="btn" onClick={onBack}>Not now</button>
                <button className="btn btn--primary ing-gold-btn" onClick={applySchedule} disabled={blocked || submitting}>
                  {confirming ? 'Confirm approval' : 'Apply schedule revision'}
                </button>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  const entityConf = pct(job?.entity_confidence);
  const lowMatch = job?.entity_confidence != null && Number(job.entity_confidence) < 0.92;

  return (
    <>
      <button className="ingest-back" onClick={onBack}>← Ingest queue</button>
      <div className="ingest-review">
        <div className="ingest-rev-head ing-navy-head">
          <span className="ingest-rev-name" title={job?.original_filename}>{job?.original_filename}</span>
          {plan?.risk_class ? <span className={`ingest-chip ingest-chip-${plan.risk_class}`}>{plan.risk_class}</span> : null}
          {job?.doc_type ? <span className="ingest-chip ingest-chip-info">{humanize(job.doc_type)}</span> : null}
          {entityConf ? (
            <span className={`ingest-rev-match${lowMatch ? ' warn' : ''}`}>entity match <b>{entityConf}</b></span>
          ) : null}
          <span className="ingest-rev-meta">
            {[
              job?.target_entity_type ? humanize(job.target_entity_type) : null,
              job?.sha256 ? `sha256 ${job.sha256.slice(0, 16)}` : null,
              job?.model_id || null,
              job?.prompt_version ? `prompt ${job.prompt_version}` : null,
            ].filter(Boolean).join(' · ')}
          </span>
        </div>

        {plan?.recommendation ? (
          <div className={`ingest-rec ingest-rec-${REC_TONE[plan.recommendation.disposition] || 'elevated'}`}>
            <div className="ingest-rec-head">
              <span className="ingest-rec-verb">{REC_VERB[plan.recommendation.disposition] || 'Recommendation'}</span>
              {plan.recommendation.confidence != null ? (
                <span className="ingest-rec-conf">{Math.round(plan.recommendation.confidence * 100)}% confident</span>
              ) : null}
              <span className="ingest-rec-lab">AI · advisory</span>
            </div>
            {plan.recommendation.rationale ? <p className="ingest-rec-why">{plan.recommendation.rationale}</p> : null}
            {Array.isArray(plan.recommendation.checks) && plan.recommendation.checks.length ? (
              <ul className="ingest-rec-checks">
                {plan.recommendation.checks.map((c: any, i: number) => (
                  <li key={i} className={c.ok ? 'ok' : 'no'}>
                    <span aria-hidden="true">{c.ok ? '✓' : '✕'}</span> {c.label}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {Array.isArray(plan?.warnings) && plan.warnings.length ? (
          <div className="ingest-warnings">
            {plan.warnings.map((w: any, i: number) => (
              <div key={i} className={`ingest-warn ingest-warn-${w.severity === 'high' ? 'high' : 'elevated'}`}>
                <span className="ingest-warn-icon" aria-hidden="true">⚠</span>
                <span className="ingest-warn-msg">{w.message}</span>
              </div>
            ))}
          </div>
        ) : null}

        {plan?.rate_flags?.anyOver ? (
          <div className="ingest-rateflag">
            <div className="ingest-rateflag-head">
              <span className="ingest-rateflag-title">⚠ Billed over the agreed rate</span>
              <span className="ingest-rateflag-total">
                {money(plan.rate_flags.totalOverAmount)} over on {plan.rate_flags.overCount} line{plan.rate_flags.overCount === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="ingest-rateflag-list">
              {plan.rate_flags.flags.filter((f: any) => f.over).map((f: any, i: number) => (
                <li key={i}>
                  <span className="ingest-rateflag-lcat">{f.lcat}</span>
                  {' billed '}<b>{money(f.billed)}/hr</b>{' against the '}{money(f.ceiling)}/hr {f.source === 'lcat_baseline' ? 'baseline' : 'rate card'}
                  {' — '}<b className="over">+{money(f.deltaPerHour)}/hr</b>
                  {f.deltaAmount != null ? <> · <b className="over">+{money(f.deltaAmount)}</b> on {f.hours} hrs</> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {blocked ? (
          <div className="ingest-blocker">
            <b>⛔ Cannot apply</b>
            <span>{blockers.map((b: any) => (typeof b === 'string' ? b : b.detail || b.message || JSON.stringify(b))).join(' ')}</span>
          </div>
        ) : null}

        {blast ? (
          <div className="ingest-blast">
            <span className="ingest-blast-lab">Blast radius</span>
            <span>{blast}</span>
          </div>
        ) : null}

        <div className="ing-doc-pane">
          <div className="ing-doc-page" ref={pageRef}>
            {items.map((item) => {
              const f = itemFields(item)[0];
              if (!f) return null;
              return (
                <div key={item.id} className="ing-doc-line" id={`ing-anchor-${item.id}`}>
                  <span className="ing-doc-k">{humanize(f[0])}</span>{' '}
                  {fmtValue(f[0], f[1])}
                </div>
              );
            })}
          </div>
          <AnnotationLayer
            pageRef={pageRef}
            disabled={readOnly}
            annotations={items.filter((it) => it.confidence != null && Number(it.confidence) < 0.9).map((it) => {
              const f0 = itemFields(it)[0];
              return {
                id: it.id,
                anchorId: `ing-anchor-${it.id}`,
                severity: 'warn',
                title: f0 ? humanize(f0[0]) : targetLabel(it),
                body: `${humanize(it.op)} · ${targetLabel(it)} · ${pct(it.confidence)} confidence`,
                selected: selectedItemId === it.id,
              };
            })}
            onSelect={setSelectedItemId}
          />
        </div>

        <div className="ingest-items">
          {items.length === 0 ? (
            <div className="state"><p className="state__body">This document produced no proposed changes.</p></div>
          ) : (
            items.map((item) => {
              const dec = decisionOf(item.id);
              const isEditing = editing === item.id;
              const source = isEditing ? edits[item.id] : item.proposed_value;
              const fields = isEditing
                ? Object.entries(source || {})
                : itemFields({ ...item, proposed_value: source });
              const conf = pct(item.confidence);
              const lowConf = item.confidence != null && Number(item.confidence) < 0.9;
              return (
                <div key={item.id} className={`ingest-item${dec === 'rejected' ? ' rejected' : ''}`}>
                  <div className="ingest-item-top">
                    <span className="ingest-item-op">{item.op}</span>
                    <span className="ingest-item-target">{targetLabel(item)}</span>
                    {conf ? (
                      <span className={`ingest-item-conf${lowConf ? ' warn' : ''}`}>
                        <svg className="ing-conf-ring" viewBox="0 0 20 20" aria-hidden="true">
                          <circle className="ing-conf-ring-bg" cx="10" cy="10" r="7.5" />
                          <circle
                            className="ing-conf-ring-fg"
                            cx="10" cy="10" r="7.5"
                            strokeDasharray={2 * Math.PI * 7.5}
                            strokeDashoffset={2 * Math.PI * 7.5 * (1 - (item.confidence ?? 0))}
                          />
                        </svg>
                        {conf}
                      </span>
                    ) : null}
                    {readOnly ? (
                      item.status ? <span className={`ingest-chip ingest-chip-${item.status === 'rejected' ? 'critical' : 'low'}`}>{item.status}</span> : null
                    ) : (
                      <div className="ingest-item-acts">
                        <button
                          className={`ingest-act${dec === 'accepted' && !isEditing ? ' on-ok' : ''}`}
                          title="Accept" aria-label="Accept" onClick={() => setDecision(item.id, 'accepted')}
                        >✓</button>
                        <button
                          className={`ingest-act${isEditing ? ' on-ed' : ''}`}
                          title="Edit" aria-label="Edit" onClick={() => (isEditing ? setEditing(null) : startEdit(item))}
                        >✎</button>
                        <button
                          className={`ingest-act${dec === 'rejected' ? ' on-no' : ''}`}
                          title="Reject" aria-label="Reject" onClick={() => setDecision(item.id, 'rejected')}
                        >✕</button>
                      </div>
                    )}
                  </div>
                  <div className="ingest-fields">
                    {fields.map(([key, val]) => (
                      <div
                        key={key}
                        className="ingest-field-row"
                        data-item-field={item.id}
                        onClick={() => setSelectedItemId(item.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedItemId(item.id); }
                        }}
                      >
                        <div className="ingest-field-k">{humanize(key)}</div>
                        <div className="ingest-field-v">
                          {isEditing ? (
                            <input
                              className="ingest-field-edit"
                              value={val == null ? '' : String(val)}
                              onChange={(e) => editField(item.id, key, e.target.value)}
                              aria-label={humanize(key)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <>
                              {item.before_value && item.before_value[key] != null ? (
                                <span className="before">{fmtValue(key, item.before_value[key])}</span>
                              ) : null}
                              {fmtValue(key, val)}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="ingest-rev-foot">
          {readOnly ? (
            <>
              <span className="ingest-foot-note">
                {job?.status === 'committed'
                  ? `Committed · ${items.filter((it) => it.status !== 'rejected').length} change${items.filter((it) => it.status !== 'rejected').length === 1 ? '' : 's'} written`
                  : humanize(job?.status || 'settled')}
              </span>
              <span className="ingest-foot-push" />
              <button className="btn btn--primary" onClick={onBack}>Back to queue</button>
            </>
          ) : (
            <>
              <span className="ingest-foot-note">
                {acceptedCount} accepted · {rejectedCount} rejected{isCritical ? ' · second confirmation required' : ''}
              </span>
              <span className="ingest-foot-push" />
              <button className="btn btn--danger" onClick={rejectDocument} disabled={submitting}>
                Reject document
              </button>
              <button className="btn btn--primary ing-gold-btn" onClick={approve} disabled={blocked || submitting || items.length === 0}>
                {blocked
                  ? 'Blocked — resolve first'
                  : confirming
                    ? 'Confirm approval'
                    : `Approve ${acceptedCount} change${acceptedCount === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export { blastText, itemFields, targetLabel };
